import React, { useEffect, useMemo, useState } from "react";
import { GoogleMap, Polyline, Marker, useJsApiLoader } from "@react-google-maps/api";
import "../css/RouteSelector.css";
import { GOOGLE_MAPS_LIBRARIES } from "../utils/config";

export default function RouteSelector({ from, to, onSelect }) {
  const [routes, setRoutes] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [error, setError] = useState("");

  const [startCoord, setStartCoord] = useState(null);
  const [endCoord, setEndCoord] = useState(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (!isLoaded || !from || !to) {
      setRoutes([]);
      setStartCoord(null);
      setEndCoord(null);
      setError("");
      return;
    }

    const svc = new window.google.maps.DirectionsService();
    svc.route(
      {
        origin: from,
        destination: to,
        travelMode: window.google.maps.TravelMode.DRIVING,
        provideRouteAlternatives: true,
      },
      (result, status) => {
        if (status !== "OK") {
          console.error("Directions failed:", status);
          setRoutes([]);
          setStartCoord(null);
          setEndCoord(null);
          setError(`No routes found for "${from}" to "${to}".`);
          return;
        }

        const firstLeg = result.routes[0].legs[0];
        setStartCoord({ lat: firstLeg.start_location.lat(), lng: firstLeg.start_location.lng() });
        setEndCoord({ lat: firstLeg.end_location.lat(), lng: firstLeg.end_location.lng() });

        const normalized = result.routes.map((r) => {
          const leg = r.legs[0];
          const waypoints = [
            { location: leg.start_address, coordinates: { lat: leg.start_location.lat(), lng: leg.start_location.lng() } },
            ...(leg.via_waypoints || []).map(via => ({ location: via.toString(), coordinates: { lat: via.lat(), lng: via.lng() } })),
            { location: leg.end_address, coordinates: { lat: leg.end_location.lat(), lng: leg.end_location.lng() } },
          ];
          const geometry = r.overview_path.map(pt => ({ lat: pt.lat(), lng: pt.lng() }));

          return {
            path: r.overview_path,
            distanceText: leg.distance?.text || "",
            durationText: leg.duration?.text || "",
            summary: r.summary || `Route`,
            route_data: { waypoints, geometry },
          };
        });
        
        setRoutes(normalized);
        
        // Auto-select the first (fastest) route so it's beautifully blue right away!
        setSelectedIndex(0);
        onSelect?.(normalized[0]); 
        
        setError("");
      }
    );
  }, [isLoaded, from, to]);

  const center = useMemo(() => ({ lat: 22.9734, lng: 78.6569 }), []);

  function handlePick(i) {
    setSelectedIndex(i);
    onSelect?.(routes[i]);
  }

  return (
    <div className="route-selector">
      {!from || !to ? (
        <div className="route-hint">Enter From and To to see routes</div>
      ) : error ? (
        <div className="route-hint" style={{ color: "red" }}>{error}</div>
      ) : (
        <>
          {isLoaded && (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "320px", borderRadius: 12 }}
              zoom={6}
              center={startCoord || center} 
              options={{
                streetViewControl: false,
                mapTypeControl: false,
                fullscreenControl: false,
              }}
            >
              {/* Map Pins */}
              {startCoord && <Marker position={startCoord} />}
              {endCoord && <Marker position={endCoord} />}

              {/* Attractive 3D Map Lines for ALL Routes */}
              {routes.map((r, i) => {
                const isActive = selectedIndex === i;
                
                return (
                  <React.Fragment key={i}>
                    {/* BOTTOM LAYER: The thick outer border */}
                    <Polyline
                      path={r.path}
                      options={{
                        // Dark blue border if active, dark grey border if alternative route
                        strokeColor: isActive ? "#174EA6" : "#174EA6", 
                        strokeOpacity: 1.0,
                        strokeWeight: 8, // Thick border
                        strokeLinecap: "round",
                        strokeLinejoin: "round",
                        zIndex: isActive ? 2 : 1,
                        clickable: true,
                      }}
                      onClick={() => handlePick(i)}
                    />
                    
                    {/* TOP LAYER: The light inner color filling */}
                    <Polyline
                      path={r.path}
                      options={{
                        // Vibrant light blue fill if active, light grey fill if alternative route
                        strokeColor: isActive ? "#4A89F3" : "#dbdfe28a", 
                        strokeOpacity: 1.0,
                        strokeWeight: 4, // Thinner so the border shows on the sides!
                        strokeLinecap: "round",
                        strokeLinejoin: "round",
                        zIndex: isActive ? 3 : 2, // Always perfectly on top of its border
                        clickable: false, // Prevents blocking clicks on the bottom layer
                      }}
                    />
                  </React.Fragment>
                );
              })}
            </GoogleMap>
          )}

          {routes.length > 0 && (
            <div className="route-pills">
              
              {/* Beautiful Styled Info Banner */}
              <div style={{ 
                marginBottom: "12px", 
                fontSize: "14px", 
                color: "#174EA6",             
                backgroundColor: "#E8F0FE",   
                border: "1px solid #8AB4F8",  
                borderLeft: "5px solid #1A73E8", 
                padding: "10px 14px", 
                borderRadius: "8px",
                fontWeight: "500",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <span style={{ fontSize: "16px" }}>🗺️</span>
                {routes.length} route{routes.length > 1 ? "s" : ""} found. Click a route on the map or pill to select.
              </div>

              {routes.map((r, i) => (
                <button
                  key={i}
                  className={`pill ${selectedIndex === i ? "active" : ""}`}
                  onClick={() => handlePick(i)}
                >
                  <span className="pill-summary">{r.summary || `Route ${i + 1}`}</span>
                  <span className="pill-meta">
                    {r.durationText} • {r.distanceText}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}